import { useState } from 'react';
import { Ban, Send } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMessageStore } from '@/stores/message-store';
import { OPT_OUT_SMS_WARNINGS, isOptOutSmsChannel, optOutComposeKey } from '@/lib/utils/opt-out';
import { OptOutSmsWarning } from './optout-sms-warning';

interface ComposeOptOutNoticeProps {
  /** The agent confirmed — send the held-back message (`send({ optOutSendAnyway: true })`). */
  onSendAnyway: () => void;
  className?: string;
}

/**
 * KAN-155: Compose held back a message to ONE customer who opted out. The agent
 * decides, in the same two steps as the inbox chat: "Send anyway" → "Yes, send
 * anyway". Shown only while the form still names that sender and customer —
 * change either and the notice is gone.
 */
export function ComposeOptOutNotice({ onSendAnyway, className }: ComposeOptOutNoticeProps) {
  const block = useMessageStore((s) => s.optOutBlock);
  const sender = useMessageStore((s) => s.sender);
  const channel = useMessageStore((s) => s.channel);
  const recipients = useMessageStore((s) => s.recipients);
  // Remembered per held-back message, so a half-open "Send anyway?" never
  // carries over to another customer.
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  if (!block || block.key !== optOutComposeKey(sender, channel, recipients)) return null;
  const confirming = confirmingKey === block.key;

  return (
    <div
      data-testid="compose-optout-notice"
      className={cn('flex items-start gap-2 rounded-lg border border-rose-300/50 bg-rose-500/5 p-3', className)}
    >
      <Ban className="h-4 w-4 shrink-0 mt-0.5 text-rose-500" />
      <div className="flex-1 min-w-0 space-y-2">
        {confirming ? (
          <>
            <p className="text-xs text-muted-foreground">
              This customer asked not to be messaged.{' '}
              <span className="font-medium text-foreground">Send anyway?</span>
            </p>
            {isOptOutSmsChannel(sender?.channel || channel) && (
              <OptOutSmsWarning text={OPT_OUT_SMS_WARNINGS.sendAnyway} testId="compose-optout-sms-warning" />
            )}
          </>
        ) : (
          <div className="space-y-0.5">
            <p className="text-sm font-medium text-foreground">Not sent — customer opted out</p>
            <p className="text-xs text-muted-foreground">
              They opted out
              {block.time
                ? ` on ${new Date(block.time).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
                : ''}
              . They can reply START to get messages again.
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {confirming ? (
            <>
              <button
                type="button"
                data-testid="compose-optout-confirm"
                onClick={() => { setConfirmingKey(null); onSendAnyway(); }}
                className="flex items-center gap-1.5 rounded-lg border border-rose-300/50 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/20 transition-colors"
              >
                <span>Yes, send anyway</span>
              </button>
              <button
                type="button"
                onClick={() => setConfirmingKey(null)}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
              >
                <span>Cancel</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid="compose-optout-send-anyway"
              onClick={() => setConfirmingKey(block.key)}
              className="flex items-center gap-1.5 rounded-lg border border-rose-300/50 bg-rose-500/5 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors"
            >
              <Send className="h-3.5 w-3.5" />
              <span>Send anyway</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
